import {
  type JSX,
  createSignal,
  createEffect,
  createMemo,
  Show,
  For,
  on,
  batch,
  onCleanup,
} from 'solid-js';
// eslint-disable-next-line @nx/enforce-module-boundaries -- TagEditor and ITagItem are required for the sharing modal layout
import { TagEditor, type ITagItem } from '@tcweb/connect-ui';
import type { IProjectUser, IProjectGroup } from '../../utils/util';
import type {
  IssueAclMap,
  IssueAccessBucket,
  IPermissionEntry,
} from '../../types/issue-permissions.types';
import {
  MAX_ISSUE_ACL_IDENTITIES,
  PERMISSION_CONSTANTS,
} from '../../types/issue-permissions.types';
import {
  resolvePermissionsToEntries,
  buildIssueAclPayload,
  normalizeIssueAcl,
  parseExplicitIssueAcl,
  buildPatchPermissionsBody,
  prepareMultiIssuePermissionDetails,
  buildBatchIssueAclForIssue,
  countIssueAclIdentities,
  getAvatarInitials,
  normalizeToFullIdentity,
} from '../../utils/issue-permissions.util';
import { AccessLevelDropdown } from '../AccessLevelDropdown/AccessLevelDropdown';
import { useSnackbar } from '../ConnectSnackbar';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Generated SDK functions needed for permissions API calls
import {
  type IssuePermissions,
  getIssuePermissions,
  patchIssuePermissions,
} from '@data-access/connect/tc-issue-management';
import { useApiServices } from '@trimble/tc-issue-management-providers';
import { translate } from '@fulcrum/lib';
import './IssueSharingModal.css';

export interface IssueSharingModalProps {
  readonly show: boolean;
  readonly issueIds: readonly string[];
  readonly contextId: string;
  onClose(): void;
  onSave(): void;
  readonly projectUsersData: readonly IProjectUser[];
  readonly projectGroupsData?: readonly IProjectGroup[];
  readonly allAssignees: readonly ITagItem[];
  readonly batchMode?: boolean;
  /** Defense-in-depth gate: when explicitly false, handleSave exits early. */
  readonly canSave?: boolean;
  /** Raw tiduuid of the issue creator, used to show "Full access" vs "Edit access" in the inherited section. */
  readonly creatorIdentity?: string;
}

interface PerIssueData {
  readonly direct: IPermissionEntry[];
  readonly inherited: IPermissionEntry[];
}

const VALID_IDENTITY_PREFIX_REGEX = /^(users:|tc-groups:).+$/;
const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;

const tagItemToIdentity = (tag: ITagItem): string => {
  if (tag.id === '*' || tag.value === '*') {
    return PERMISSION_CONSTANTS.ALL_PROJECT_MEMBERS_ID;
  }
  if (tag.tiduuid) {
    return `${PERMISSION_CONSTANTS.USER_PREFIX}${tag.tiduuid}`;
  }
  if (tag.id) {
    return `${PERMISSION_CONSTANTS.GROUP_PREFIX}${tag.id}`;
  }
  if (VALID_IDENTITY_PREFIX_REGEX.test(tag.value)) {
    return tag.value;
  }
  return `${PERMISSION_CONSTANTS.USER_PREFIX}${tag.value}`;
};

export function IssueSharingModal(props: IssueSharingModalProps): JSX.Element {
  const { showSnackbar } = useSnackbar();
  const apiServices = useApiServices();
  const [isLoading, setIsLoading] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);
  const [directPermissions, setDirectPermissions] = createSignal<
    IPermissionEntry[]
  >([]);
  const [inheritedPermissions, setInheritedPermissions] = createSignal<
    IPermissionEntry[]
  >([]);
  const [inheritedIdentities, setInheritedIdentities] = createSignal<
    Set<string>
  >(new Set<string>());
  let perIssueData: PerIssueData[] = [];
  // Tracks issueIds whose GET permissions request failed. PATCHing from an
  // empty fallback state would silently clear their ACL, so we block saves for
  // these until the modal is reopened and permissions are reloaded.
  let failedGetIssueIds: Set<string> = new Set();
  // Single-issue equivalent of failedGetIssueIds: blocks Save when the GET
  // permissions request failed, so we never PATCH from the empty fallback
  // state and silently wipe the issue's ACL.
  let singleIssueLoadFailed = false;

  // createdBy may arrive as a bare tiduuid or already as "users:{tiduuid}";
  // normalizeToFullIdentity handles both so we never double-prefix.
  const creatorFullIdentity = createMemo(() =>
    normalizeToFullIdentity(props.creatorIdentity)
  );

  const tagList = createMemo((): ITagItem[] => {
    const allProjectMembersTag: ITagItem = {
      label: translate('TC.Common.AllProjectMem') || 'All project members',
      value: '*',
      id: '*',
      isValid: true,
    };
    return [allProjectMembersTag, ...props.allAssignees];
  });

  const tagListForEditor = createMemo((): ITagItem[] => {
    const inherited = inheritedIdentities();
    const directIds = new Set(
      directPermissions().map((entry) => entry.identity)
    );
    return tagList().filter((tag) => {
      const identity = tagItemToIdentity(tag);
      if (directIds.has(identity)) return false;
      if (!props.batchMode && inherited.has(identity)) return false;
      return true;
    });
  });

  const resetState = (): void => {
    batch(() => {
      setDirectPermissions([]);
      setInheritedPermissions([]);
      setInheritedIdentities(new Set<string>());
    });
    perIssueData = [];
    failedGetIssueIds = new Set();
    singleIssueLoadFailed = false;
  };

  const loadSingleIssuePermissions = async (issueId: string): Promise<void> => {
    const response = await getIssuePermissions({
      client: apiServices.issueManagementClient,
      path: { contextId: props.contextId, issueId },
      query: { fields: ['inheritedAcl'] },
    });

    if (!response.data) {
      resetState();
      return;
    }

    const acl = parseExplicitIssueAcl(response.data.acl);
    const inheritedAcl = response.data.inheritedAcl as IssueAclMap | undefined;

    const { direct, inherited } = resolvePermissionsToEntries(
      acl,
      inheritedAcl,
      props.projectUsersData,
      props.projectGroupsData ?? []
    );

    const identSet = new Set<string>();
    inherited.forEach((e) => identSet.add(e.identity));

    batch(() => {
      setDirectPermissions(direct);
      setInheritedPermissions(inherited);
      setInheritedIdentities(identSet);
    });
  };

  const BATCH_LOAD_CONCURRENCY = 5;

  const loadBatchPermissions = async (): Promise<void> => {
    const issueIds = [...props.issueIds];
    const contextId = props.contextId;
    const projectUsersData = props.projectUsersData;
    const projectGroupsData = props.projectGroupsData ?? [];

    const fetchOne = async (
      issueId: string
    ): Promise<IssuePermissions | null> => {
      try {
        const response = await getIssuePermissions({
          client: apiServices.issueManagementClient,
          path: { contextId, issueId },
          query: { fields: ['inheritedAcl'] },
        });
        return response.data ?? null;
      } catch (error) {
        console.error(
          'Failed to fetch issue permissions for issue',
          issueId,
          error
        );
        failedGetIssueIds.add(issueId);
        return null;
      }
    };

    // Chunk requests to cap concurrency and avoid flooding the server.
    const rawResults: (IssuePermissions | null)[] = [];
    for (let i = 0; i < issueIds.length; i += BATCH_LOAD_CONCURRENCY) {
      const chunk = issueIds.slice(i, i + BATCH_LOAD_CONCURRENCY);
      const chunkResults = await Promise.all(chunk.map(fetchOne));
      rawResults.push(...chunkResults);
    }

    const allPerIssue: PerIssueData[] = rawResults.map((data) => {
      if (!data) return { direct: [], inherited: [] };
      const acl = parseExplicitIssueAcl(data.acl);
      const inheritedAcl = data.inheritedAcl as IssueAclMap | undefined;
      return resolvePermissionsToEntries(
        acl,
        inheritedAcl,
        projectUsersData,
        projectGroupsData
      );
    });

    perIssueData = allPerIssue;

    const { consolidated, inheritedUnion } =
      prepareMultiIssuePermissionDetails(allPerIssue);

    const identSet = new Set<string>();
    inheritedUnion.forEach((e) => identSet.add(e.identity));

    batch(() => {
      setDirectPermissions(consolidated);
      setInheritedPermissions(inheritedUnion);
      setInheritedIdentities(identSet);
    });
  };

  let loadRequestId = 0;
  let saveRequestId = 0;

  const loadPermissions = async (): Promise<void> => {
    const requestId = ++loadRequestId;
    setIsLoading(true);
    try {
      if (props.batchMode) {
        if (props.issueIds.length === 0) {
          resetState();
          return;
        }
        await loadBatchPermissions();
      } else {
        if (props.issueIds.length !== 1) {
          resetState();
          return;
        }
        singleIssueLoadFailed = false;
        await loadSingleIssuePermissions(props.issueIds[0]);
      }
      if (requestId !== loadRequestId) return;
    } catch {
      if (requestId !== loadRequestId) return;
      if (!props.batchMode) {
        singleIssueLoadFailed = true;
      }
      console.error('Failed to load issue permissions');
      showSnackbar({
        text:
          translate('TC.Common.TryAgainLater') || 'Failed to load permissions',
        type: 'error',
      });
    } finally {
      if (requestId === loadRequestId) {
        setIsLoading(false);
      }
    }
  };

  createEffect(
    on(
      () => props.show,
      (show) => {
        if (show) {
          void loadPermissions();
        } else {
          loadRequestId++;
          resetState();
        }
      }
    )
  );

  // Called by TagEditor when user picks a suggestion. Since we pass
  // selectedTagList={[]} the callback receives only the newly selected tag(s).
  // We convert each to IPermissionEntry and append to directPermissions.
  const handleTagChange = (tags: ITagItem[]): void => {
    if (tags.length === 0) return;

    const existing = directPermissions();
    const existingIds = new Set(existing.map((entry) => entry.identity));

    const additions: IPermissionEntry[] = [];
    for (const tag of tags) {
      const identity = tagItemToIdentity(tag);
      if (existingIds.has(identity)) continue;

      const isGroup =
        tag.id === '*' ||
        identity.startsWith(PERMISSION_CONSTANTS.GROUP_PREFIX);
      additions.push({
        identity,
        displayName: tag.label,
        accessLevel: 'edit' as IssueAccessBucket,
        isInherited: false,
        avatarInitials: getAvatarInitials(tag.label),
        isGroup,
      });
      existingIds.add(identity);
    }

    if (additions.length > 0) {
      setDirectPermissions([...existing, ...additions]);
    }
  };

  const handleAccessChange = (
    index: number,
    access: IssueAccessBucket
  ): void => {
    setDirectPermissions((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, accessLevel: access, isMixed: false } : entry
      )
    );
  };

  const handleRemoveEntry = (index: number): void => {
    setDirectPermissions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveAll = (): void => {
    setDirectPermissions([]);
  };

  const handleSave = async (): Promise<void> => {
    if (props.canSave === false) return;
    const requestId = ++saveRequestId;
    setIsSaving(true);
    try {
      if (props.batchMode) {
        const issueIds = [...props.issueIds];
        const contextId = props.contextId;
        const directPerms = directPermissions();
        const results = await Promise.allSettled(
          issueIds.map(async (issueId, index) => {
            // Block save for issues whose GET failed — patching from an empty
            // fallback state would silently clear their existing ACL.
            if (failedGetIssueIds.has(issueId)) {
              return 'blocked' as const;
            }
            const { acl } = buildBatchIssueAclForIssue(
              directPerms,
              perIssueData[index]?.direct ?? []
            );
            const normalized = normalizeIssueAcl(acl);
            if (
              countIssueAclIdentities(normalized) > MAX_ISSUE_ACL_IDENTITIES
            ) {
              return false as const; // skipped — over identity limit
            }
            await patchIssuePermissions({
              client: apiServices.issueManagementClient,
              path: { contextId, issueId },
              body: buildPatchPermissionsBody(normalized),
            });
            return true as const;
          })
        );

        if (requestId !== saveRequestId) return;

        const fulfilled = results.filter((r) => r.status === 'fulfilled');
        const succeeded = fulfilled.filter((r) => r.value === true).length;
        const skipped = fulfilled.filter((r) => r.value === false).length;
        const blocked = fulfilled.filter((r) => r.value === 'blocked').length;

        if (succeeded > 0) {
          showSnackbar({
            text:
              translate('TC.Topic.SharedSuccessfully') ||
              'Topic successfully shared',
            type: 'success',
          });
          if (skipped > 0) {
            showSnackbar({
              text:
                translate('enterPositiveIntegerValueMax') ||
                'Some issues were skipped: maximum identity limit exceeded',
              type: 'warning',
            });
          }
          props.onSave();
          props.onClose();
        } else if (skipped > 0) {
          showSnackbar({
            text:
              translate('enterPositiveIntegerValueMax') ||
              'Maximum identity limit exceeded',
            type: 'error',
          });
        } else {
          showSnackbar({
            text: translate('TC.Common.TryAgainLater') || 'Failed to save',
            type: 'error',
          });
        }

        if (blocked > 0) {
          showSnackbar({
            text:
              translate('TC.Common.TryAgainLater') ||
              'Some issues could not be saved: permissions failed to load. Please close and reopen the dialog to retry.',
            type: 'warning',
          });
        }
      } else if (props.issueIds.length === 1) {
        // Block save when the GET failed — patching from the empty fallback
        // state would silently clear the issue's existing ACL.
        if (singleIssueLoadFailed) {
          showSnackbar({
            text:
              translate('TC.Common.TryAgainLater') ||
              'Permissions failed to load. Please close and reopen the dialog to retry.',
            type: 'error',
          });
          return;
        }

        const { acl } = buildIssueAclPayload(directPermissions());
        const normalized = normalizeIssueAcl(acl);

        if (countIssueAclIdentities(normalized) > MAX_ISSUE_ACL_IDENTITIES) {
          showSnackbar({
            text:
              translate('enterPositiveIntegerValueMax') ||
              'Maximum identity limit exceeded',
            type: 'error',
          });
          return;
        }

        await patchIssuePermissions({
          client: apiServices.issueManagementClient,
          path: { contextId: props.contextId, issueId: props.issueIds[0] },
          body: buildPatchPermissionsBody(normalized),
        });

        if (requestId !== saveRequestId) return;

        showSnackbar({
          text:
            translate('TCW.Common.SaveSuccessfully') || 'Saved successfully',
          type: 'success',
        });
        props.onSave();
        props.onClose();
      }
    } catch (error) {
      if (requestId !== saveRequestId) return;
      console.error('Failed to save issue permissions');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy API error shape
      const status = (error as any)?.response?.status;
      if (status === HTTP_BAD_REQUEST) {
        showSnackbar({
          text: translate('TC.Error.PermissionDenied') || 'Bad request',
          type: 'error',
        });
      } else if (status === HTTP_FORBIDDEN) {
        showSnackbar({
          text:
            translate('TC.Common.PermissionDeniedWarning') ||
            'Permission denied',
          type: 'error',
        });
      } else {
        showSnackbar({
          text:
            translate('TC.Common.TryAgainLater') ||
            'Failed to save permissions',
          type: 'error',
        });
      }
    } finally {
      if (requestId === saveRequestId) setIsSaving(false);
    }
  };

  const modalContent = (): JSX.Element => (
    <div class="issue-sharing-container">
      <Show
        when={!isLoading()}
        fallback={
          <div class="issue-sharing-loading">
            <span>{translate('TC.Common.Loading') || 'Loading...'}</span>
          </div>
        }
      >
        <TagEditor
          tagList={tagListForEditor()}
          onTagChange={(tags) => handleTagChange(tags)}
          placeHolder={
            translate('TC.Assignees.Placeholder') || 'Search users or groups'
          }
          selectedTagList={[]}
          id="issue-sharing-input"
          matchCase={false}
          title={translate('releaseShare') || 'Share with'}
        />

        <div class="selected-permissions-wrapper">
          <div class="remove-all-wrapper">
            <label>{`${translate('TC.Common.SharedWith') || 'Shared with'}:`}</label>
            <Show when={directPermissions().length > 0}>
              <span class="remove-all" onClick={handleRemoveAll}>
                {translate('TC.Common.RemoveAll') || 'Remove all'}
              </span>
            </Show>
          </div>

          <div class="users-groups-list-header">
            <ul
              class={`list selected-file-permissions ${
                inheritedPermissions().length > 0
                  ? 'with-inherited-permissions'
                  : 'without-inherited-permissions'
              }`}
            >
              <Show
                when={directPermissions().length > 0}
                fallback={
                  <p class="no-permissions-msg">
                    No permissions set on this Topic
                  </p>
                }
              >
                <For each={directPermissions()}>
                  {(entry, index) => (
                    <li class="list-item group">
                      <div class="group-items px-0">
                        <div class="chips non-editable circle mb-0 text-semibold">
                          <span>{entry.avatarInitials}</span>
                        </div>
                        <div class="block mr-2" title={entry.displayName}>
                          {entry.displayName}
                        </div>
                        <AccessLevelDropdown
                          value={entry.accessLevel}
                          isMixed={entry.isMixed}
                          onChange={(access) =>
                            handleAccessChange(index(), access)
                          }
                        />
                        <button
                          class="button tertiary icon-circle icon-medium"
                          onClick={() => handleRemoveEntry(index())}
                        >
                          <i class="icon-font tc-icon-content-close" />
                        </button>
                      </div>
                    </li>
                  )}
                </For>
              </Show>
            </ul>
          </div>

          <Show when={inheritedPermissions().length > 0}>
            <hr class="inherited-permission-hr" />
            <div class="inherited-permission-wrapper">
              <div class="inherited-permission">
                <For each={inheritedPermissions()}>
                  {(entry) => (
                    <div class="parent-folder-permission-list">
                      <div class="users-groups-name-wrapper">
                        <i class="group-avatar icon-font tc-icon-user-group" />
                        <div class="users-groups-name">
                          <span class="name-list" title={entry.displayName}>
                            {entry.displayName}
                          </span>
                          <span class="inherited-tag">
                            (
                            {translate('TC.PermissionOverview.Inherited') ||
                              'Inherited'}
                            )
                          </span>
                        </div>
                      </div>
                      <i class="group-avatar icon-font tc-icon-lock lock-icon" />
                      <div class="access-chip">
                        <span class="access">
                          {entry.identity === creatorFullIdentity()
                            ? translate('TC.Common.FullAccess') || 'Full access'
                            : translate('TC.Common.EditAccess') ||
                              'Edit access'}
                        </span>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </Show>

          <Show when={props.batchMode}>
            <div class="batch-sharing-note">
              <span class="small">
                {translate('TC.Topic.InheritedAccessNote') ||
                  'Please note: Inherited access may apply to only some selected topics.'}
              </span>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );

  let popupRef: HTMLElement | undefined;

  const handlePopupClose = (): void => {
    props.onClose();
  };
  const handlePopupSecondary = (): void => {
    props.onClose();
  };
  const handlePopupPrimary = (): void => {
    void handleSave();
  };

  onCleanup(() => {
    if (popupRef) {
      popupRef.removeEventListener('popup-close', handlePopupClose);
      popupRef.removeEventListener(
        'popup-secondary-action',
        handlePopupSecondary
      );
      popupRef.removeEventListener('popup-primary-action', handlePopupPrimary);
    }
  });

  return (
    <div class="issue-sharing-modal-container">
      {/* @ts-expect-error -- tc-popup is a custom web component not recognized by TypeScript */}
      <tc-popup
        show={props.show}
        heading={
          translate('TC.Topic.ShareTopicIssueWith') || 'Share topic/issue with'
        }
        name="issueSharingModal"
        overflow-class="overflow-unset"
        hide-footer={false}
        primary-btn-label={translate('TC.Common.Save') || 'Save'}
        primary-btn-class="primary"
        primary-btn-disabled={isSaving()}
        secondary-btn-label={translate('TC.Common.Cancel') || 'Cancel'}
        secondary-btn-class="link-secondary"
        content={modalContent()}
        ref={(el: HTMLElement) => {
          if (popupRef) {
            popupRef.removeEventListener('popup-close', handlePopupClose);
            popupRef.removeEventListener(
              'popup-secondary-action',
              handlePopupSecondary
            );
            popupRef.removeEventListener(
              'popup-primary-action',
              handlePopupPrimary
            );
          }
          popupRef = el;
          if (el) {
            el.addEventListener('popup-close', handlePopupClose);
            el.addEventListener('popup-secondary-action', handlePopupSecondary);
            el.addEventListener('popup-primary-action', handlePopupPrimary);
          }
        }}
      />
    </div>
  );
}
